import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { WebPageSchema, BreadcrumbListSchema, ArticleSchema } from '@/components/feature/SeoSchema';
import { getArticleById, getRelatedArticles, getCategoryBySlug } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const article = getArticleById(id || '');

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = getCategoryBySlug(article.category);
  const relatedArticles = getRelatedArticles(article.id, 3);
  const categoryName = category?.name || article.category;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <WebPageSchema
        title={`${article.title} - 琉璃神社官方`}
        description={article.excerpt}
        url={`https://liulishenshe.com/article/${article.id}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://liulishenshe.com' },
          { name: categoryName, url: `https://liulishenshe.com/category/${article.category}` },
          { name: article.title, url: `https://liulishenshe.com/article/${article.id}` },
        ]}
      />
      <ArticleSchema article={article} url={`https://liulishenshe.com/article/${article.id}`} />

      <Navbar />

      <main>
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="text-xs text-primary-500 bg-primary-100 px-3 py-1 rounded-full font-medium hover:bg-primary-200 transition-colors"
              >
                {categoryName}
              </Link>
              {article.isPinned && (
                <span className="text-xs text-accent-600 bg-accent-100 px-3 py-1 rounded-full font-medium">
                  置顶
                </span>
              )}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-xs"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-xs"></i>
                </div>
                {article.publishDate}
              </span>
            </div>
          </div>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 琉璃神社官方`}
              className="w-full object-cover max-h-96"
            />
          </div>

          <div
            className="prose prose-sm max-w-none text-foreground-800 leading-relaxed mb-10
              [&>p]:mb-4 [&>p]:text-sm [&>p]:leading-relaxed
              [&>h3]:font-heading [&>h3]:text-lg [&>h3]:font-bold [&>h3]:text-foreground-950 [&>h3]:mt-8 [&>h3]:mb-4
              [&>ul]:mb-4 [&>ul]:pl-5 [&>ul>li]:text-sm [&>ul>li]:leading-relaxed [&>ul>li]:mb-1.5 [&>ul>li]:list-disc
              [&>ol]:mb-4 [&>ol]:pl-5 [&>ol>li]:text-sm [&>ol>li]:leading-relaxed [&>ol>li]:mb-1.5 [&>ol>li]:list-decimal
              [&>strong]:text-foreground-950 [&>strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap items-center gap-2 mb-10 pt-6 border-t border-background-200">
            <span className="text-sm text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200 pt-10 mb-10">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        title={`${rel.title} - 琉璃神社官方`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors text-xs leading-snug line-clamp-2 mb-1">
                        {rel.title}
                      </h4>
                      <span className="text-foreground-400 text-xs">{rel.publishDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
}